import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

type ModalSize = "sm" | "md" | "lg" | "xl" | "viewer"

interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: React.ReactNode
  /** Accessible name for the dialog when the visible heading lives inside
   *  the modal body rather than the header slot (e.g. an icon-above-title
   *  layout). Renders a visually-hidden DialogTitle so Radix has an
   *  accessible name without affecting layout. Ignored when `title` is also
   *  set. */
  srOnlyTitle?: string
  description?: string
  size?: ModalSize
  className?: string
  footer?: React.ReactNode
  children?: React.ReactNode
  /** Disables the corner close X, e.g. while the dialog's action is in
   *  flight. Escape and a click outside still reach `onOpenChange`, for
   *  the caller to refuse. */
  closeDisabled?: boolean
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  // Image-forward dialogs where the content should dominate the viewport, like
  // the asset viewer (asset-view-modal.tsx). `w-auto` is load-bearing: it
  // overrides DialogContent's base `w-[calc(100%-2rem)]` so the dialog
  // shrinks to fit its content (usually a height-capped image) instead of
  // sitting permanently at the 95vw ceiling with the content marooned in the
  // middle. The min-width keeps form rows from collapsing on portrait images.
  viewer:
    "w-auto min-w-[min(90vw,720px)] max-w-[95vw] max-h-[90vh] overflow-y-auto",
}

/**
 * Prop-driven modal over Dialog — the one-liner alternative to composing Dialog parts.
 * Title/description/action buttons cover the common confirmation case, but `children`
 * accepts arbitrary content and `size` spans `sm`–`xl` plus `viewer`, a near-fullscreen
 * variant for image-forward dialogs where the content should dominate the viewport.
 *
 * @category Overlays
 */
function Modal({
  open,
  onOpenChange,
  title,
  srOnlyTitle,
  description,
  size = "md",
  className,
  footer,
  children,
  closeDisabled,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(sizeClasses[size], className)}
        closeDisabled={closeDisabled}
      >
        {title ? (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </DialogHeader>
        ) : srOnlyTitle ? (
          <DialogTitle className="sr-only">{srOnlyTitle}</DialogTitle>
        ) : null}
        {children}
        {footer && <DialogFooter>{footer}</DialogFooter>}
      </DialogContent>
    </Dialog>
  )
}

export { Modal }
export type { ModalProps, ModalSize }
